const INFOBULLE_IGNORED_ROOT_SELECTOR =
  '.jdgm-widget, .jdgm-rev-widg, .jdgm-preview-badge, [class*="jdgm-"], alma-widget, shopify-chat, .shopify-payment-button'

/**
 * @param {unknown} raw
 * @returns {'absolute' | 'inline'}
 */
export function normalizeInfobullePosition(raw) {
  return raw === 'absolute' ? 'absolute' : 'inline'
}

/**
 * @param {Node} node
 * @returns {boolean}
 */
function isIgnoredInfobulleMutationNode(node) {
  if (!(node instanceof Element)) return node?.nodeType === Node.TEXT_NODE
  return Boolean(node.closest?.(INFOBULLE_IGNORED_ROOT_SELECTOR))
}

/**
 * True when a mutation batch may need a fresh infobulle inject pass.
 * Attribute-only churn and known app/widget roots are ignored.
 *
 * @param {MutationRecord[]} mutations
 * @returns {boolean}
 */
export function mutationsAffectInfobulleInject(mutations) {
  for (const mutation of mutations) {
    if (mutation.type !== 'childList') continue

    const nodes = [...mutation.addedNodes, ...mutation.removedNodes]
    if (nodes.length === 0) continue

    for (const node of nodes) {
      if (!isIgnoredInfobulleMutationNode(node)) return true
    }
  }

  return false
}

class InfobullesInjector extends HTMLElement {
  connectedCallback() {
    const configEl = this.querySelector('[type="application/json"]')
    if (!configEl) return

    try {
      const infobulles = JSON.parse(configEl.textContent)
      /** @type {string} */
      const triggerAriaLabel =
        this.dataset.infobulleAriaLabel?.trim() || 'More information'

      const inject = () => {
        for (const [index, item] of infobulles.entries()) {
          if (!item.selector || !item.templateId) continue

          let targets
          try {
            targets = document.querySelectorAll(item.selector)
          } catch (err) {
            console.warn(
              'InfobullesInjector invalid selector',
              item.selector,
              err
            )
            continue
          }
          targets.forEach((target, targetIndex) => {
            if (target.hasAttribute('data-infobulle-injected')) return
            const position = normalizeInfobullePosition(item.position)

            target.setAttribute('data-infobulle-injected', 'true')
            target.classList.add(
              position === 'absolute'
                ? 'infobulle-host--absolute'
                : 'infobulle-host'
            )

            const popoverId = `infobulle-popover-${index}-${targetIndex}`

            const wrapper = document.createElement('anchored-popover-component')
            wrapper.dataset.hoverTriggered = 'true'
            wrapper.dataset.closeOnResize = 'true'
            wrapper.className =
              position === 'absolute'
                ? 'infobulle infobulle--absolute'
                : 'infobulle'
            if (position === 'absolute') wrapper.dataset.position = 'absolute'

            const triggerBtn = document.createElement('button')
            triggerBtn.type = 'button'
            triggerBtn.className = 'infobulle__trigger'
            triggerBtn.setAttribute('ref', 'trigger')
            triggerBtn.setAttribute('popovertarget', popoverId)
            triggerBtn.setAttribute('aria-label', triggerAriaLabel)
            triggerBtn.style.anchorName = `--infobulle-anchor-${index}-${targetIndex}`

            const iconSpan = document.createElement('span')
            iconSpan.setAttribute('aria-hidden', 'true')
            iconSpan.textContent = '?'
            triggerBtn.appendChild(iconSpan)

            const popoverEl = document.createElement('div')
            popoverEl.id = popoverId
            popoverEl.className = 'infobulle__popover rte'
            popoverEl.setAttribute('ref', 'popover')
            popoverEl.setAttribute('popover', 'auto')
            popoverEl.style.positionAnchor = `--infobulle-anchor-${index}-${targetIndex}`

            wrapper.appendChild(triggerBtn)
            wrapper.appendChild(popoverEl)

            const templateEl = document.getElementById(item.templateId)
            if (templateEl && templateEl.content) {
              popoverEl.appendChild(templateEl.content.cloneNode(true))
            }

            target.appendChild(wrapper)
          })
        }
      }

      // Defer the initial injection to idle time so the popover DOM creation
      // (and the anchored-popover upgrades it triggers) does not add to Total
      // Blocking Time during page load.
      const scheduleInject = () => {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(inject, { timeout: 500 })
        } else {
          window.setTimeout(inject, 1)
        }
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', scheduleInject)
      } else {
        scheduleInject()
      }

      // Handle dynamic DOM changes (like variant switching, quick-view injection).
      // Ignore attribute-only churn and known third-party roots so scroll/tap
      // while apps mutate the DOM does not re-scan the whole document.
      this._observer = new MutationObserver((mutations) => {
        if (!mutationsAffectInfobulleInject(mutations)) return
        clearTimeout(this._injectDebounceId)
        this._injectDebounceId = window.setTimeout(inject, 250)
      })
      this._observer.observe(document.body, { childList: true, subtree: true })
    } catch (err) {
      console.warn('InfobullesInjector Error', err)
    }
  }

  disconnectedCallback() {
    clearTimeout(this._injectDebounceId)
    if (this._observer) {
      this._observer.disconnect()
    }
  }
}

if (!customElements.get('infobulles-injector')) {
  customElements.define('infobulles-injector', InfobullesInjector)
}
